// Kit by feels-like at the start, from the Open-Meteo forecast (no key, hourly, 16 days).
// Kyle's rule: read against feels-like, not air temperature.
const URL_ = (lat: number, lon: number) =>
  `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&hourly=apparent_temperature,temperature_2m,precipitation_probability,wind_speed_10m,wind_direction_10m&timezone=Europe%2FLondon&forecast_days=16&wind_speed_unit=kmh`;

type Hourly = { time: string[]; apparent_temperature: number[]; temperature_2m: number[]; precipitation_probability: number[]; wind_speed_10m: number[]; wind_direction_10m?: number[] };
let cache: { at: number; h: Promise<Hourly | null> } | null = null;

/** One request an hour, shared by every caller on the screen, so the ride card and the kit list agree. */
function hourly(lat: number, lon: number): Promise<Hourly | null> {
  if (cache && Date.now() - cache.at < 60 * 60 * 1000) return cache.h;
  const h = fetch(URL_(lat, lon)).then(async (r) => (r.ok ? ((await r.json()) as { hourly: Hourly }).hourly : null)).catch(() => null);
  cache = { at: Date.now(), h };
  h.then((v) => { if (!v && cache?.h === h) cache = null; });
  return h;
}

/** Kyle's thresholds, feels-like °C (24 October); his to change on the You page. */
export const KIT_T: [number, number, number] = [18, 15, 10];
export function kitFor(feels: number, t: [number, number, number] = KIT_T): string {
  if (feels >= t[0]) return "bib shorts and a short sleeve top";
  if (feels >= t[1]) return "bib shorts and a long sleeve top";
  if (feels >= t[2]) return "bib tights and a long sleeve top";
  return "jacket, long sleeve top and bib tights";
}
/** Thresholds can be set on the You page; every caller with the state passes them through this. */
let kitT: [number, number, number] = KIT_T;
export function setKitThresholds(t?: [number, number, number]) { kitT = t && t.length === 3 ? t : KIT_T; }

export type Forecast = { feels: number; air: number; rain: number; wind: number; kit: string };

/** The forecast at the ride's start hour, or null beyond 16 days or with no signal. */
export async function forecastAt(lat: number, lon: number, date: string, start: string): Promise<Forecast | null> {
  const h = await hourly(lat, lon);
  if (!h) return null;
  const i = h.time.indexOf(`${date}T${start.slice(0, 2)}:00`);
  if (i < 0) return null;
  const feels = Math.round(h.apparent_temperature[i]);
  return { feels, air: Math.round(h.temperature_2m[i]), rain: h.precipitation_probability[i] ?? 0, wind: Math.round(h.wind_speed_10m[i]), kit: kitFor(feels, kitT) };
}

export type Span = Forecast & { rainMax: number; windMax: number; rainFrom: string | null };
/** The first hour of the ride with a 40% chance of rain or more, as HH:00. */
function firstRain(h: Hourly, a: number, b: number): string | null {
  for (let i = a; i <= b; i++) if ((h.precipitation_probability[i] ?? 0) >= 40) return h.time[i].slice(11, 16);
  return null;
}
/** Feels-like and kit at the start; the worst rain chance and wind over the ride's hours. */
export async function forecastSpan(lat: number, lon: number, date: string, start: string, end: string): Promise<Span | null> {
  const h = await hourly(lat, lon);
  if (!h) return null;
  const a = h.time.indexOf(`${date}T${start.slice(0, 2)}:00`);
  if (a < 0) return null;
  const b = Math.max(a, h.time.indexOf(`${date}T${end.slice(0, 2)}:00`));
  const feels = Math.round(h.apparent_temperature[a]);
  let rainMax = 0, windMax = 0;
  for (let i = a; i <= b; i++) { rainMax = Math.max(rainMax, h.precipitation_probability[i] ?? 0); windMax = Math.max(windMax, Math.round(h.wind_speed_10m[i])); }
  return { feels, air: Math.round(h.temperature_2m[a]), rain: h.precipitation_probability[a] ?? 0, wind: Math.round(h.wind_speed_10m[a]), kit: kitFor(feels, kitT), rainMax, windMax, rainFrom: firstRain(h, a, b) };
}

export type HourlyDay = { feels: number[]; rain: boolean[]; wind: number[] };
/** The 17 hours from 05:00 to 21:00 for the band's weather fill, or null beyond the forecast. */
export async function hourlyDay(lat: number, lon: number, date: string, rainAt = 40): Promise<HourlyDay | null> {
  const h = await hourly(lat, lon);
  if (!h) return null;
  const a = h.time.indexOf(`${date}T05:00`);
  if (a < 0 || a + 16 >= h.time.length) return null;
  const feels: number[] = [], rain: boolean[] = [], wind: number[] = [];
  for (let i = a; i <= a + 16; i++) {
    feels.push(Math.round(h.apparent_temperature[i]));
    rain.push((h.precipitation_probability[i] ?? 0) >= rainAt);
    wind.push(Math.round(h.wind_speed_10m[i]));
  }
  return { feels, rain, wind };
}

/** The wind over a ride's hours: where it comes from (degrees, averaged as a vector) and its mean speed. */
export async function windOver(lat: number, lon: number, date: string, start: string, end: string): Promise<{ from: number; kmh: number } | null> {
  const h = await hourly(lat, lon);
  if (!h?.wind_direction_10m) return null;
  const a = h.time.indexOf(`${date}T${start.slice(0, 2)}:00`);
  if (a < 0) return null;
  const b = Math.max(a, h.time.indexOf(`${date}T${end.slice(0, 2)}:00`));
  let x = 0, y = 0, sp = 0, n = 0;
  for (let i = a; i <= b; i++) { const r = (h.wind_direction_10m[i] * Math.PI) / 180, v = h.wind_speed_10m[i]; x += Math.sin(r) * v; y += Math.cos(r) * v; sp += v; n++; }
  return { from: Math.round(((Math.atan2(x, y) * 180) / Math.PI + 360) % 360), kmh: Math.round(sp / n) };
}
